
// 25.03 oncesi// ....

import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTenant } from "../../context/TenantContext";
import RobotSelection from "../Common/RobotSelection";
import ProcessSelection from "../Common/ProcessSelection";
import FolderSelection from "../Common/FolderSelection";
import LoggingService from "../../services/LoggingService";
import config from '../../config/config';
import "../../styles/Triggers.css";
import { toast } from 'react-toastify';

const EmailTrigger = () => {
  const { selectedTenant } = useTenant();
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [filters, setFilters] = useState([{ field: "", condition: "", value: "" }]);
  const [folder, setFolder] = useState("");
  const [process, setProcess] = useState({ id: "", name: "" });
  const [robot, setRobot] = useState("All");
  const [argumentsList, setArgumentsList] = useState([]);
  const [stickToRobot, setStickToRobot] = useState(false);
  const navigate = useNavigate();

  const connectorMapping = {
    Gmail: "imap.gmail.com",
    Outlook: "imap-mail.outlook.com",
  };

  const handleAddCondition = () => {
    setFilters([...filters, { field: "", condition: "", value: "" }]);
  };

  const handleFilterChange = (index, field, value) => {
    const updatedFilters = filters.map((filter, i) =>
      i === index ? { ...filter, [field]: value } : filter
    );
    setFilters(updatedFilters);
  };

  const handleRemoveCondition = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };


  // Add this after the existing state declarations
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [priority, setPriority] = useState("Inherited");

  // Add this validation function before handleEmailTrigger
  // Update validateForm function
  // First, define validateForm correctly
  const validateForm = () => {
    const errors = {};
    if (!name.trim()) errors.name = "Trigger name is required";
    if (!platform) errors.platform = "Platform selection is required";
    if (!username) errors.username = "Email username is required";
    if (!password) errors.password = "Email password is required";
    if (!folder) errors.folder = "Folder selection is required";
    if (!process.id) errors.process = "Process selection is required";

    // Validate filters if any are present
    filters.forEach((filter, index) => {
      if (filter.field && (!filter.condition || !filter.value)) {
        errors[`filter_${index}`] = "Please complete all filter fields";
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Then define priorities array
  const priorities = [
    { value: "Inherited", label: "Inherited", icon: "○" },
    { value: "Critical", label: "Critical", icon: "◊" },
    { value: "Highest", label: "Highest", icon: "≫" },
    { value: "Very high", label: "Very high", icon: "≫" },
    { value: "High", label: "High", icon: "∧" },
    { value: "Medium-high", label: "Medium-high", icon: "⋀" },
    { value: "Medium", label: "Medium", icon: "=" },
    { value: "Medium-low", label: "Medium-low", icon: "⋁" },
    { value: "Low", label: "Low", icon: "∨" },
    { value: "Very low", label: "Very low", icon: "≪" },
    { value: "Lowest", label: "Lowest", icon: "≪" }
  ];

  // Then define handleEmailTrigger
  const handleEmailTrigger = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      let userId = localStorage.getItem("userId");
      if (!userId) {
        toast.error("User ID not found. Please try logging in again.");
        console.warn("⚠️ No user ID found in localStorage. Refreshing...");
        setTimeout(() => {
          userId = localStorage.getItem("userId");
          if (!userId) {
            console.error("❌ No user ID found in localStorage after retry. Aborting request.");
            return;
          }
        }, 1000);
        return;
      }

      const platformToSave = connectorMapping[platform] || platform;

      const emailConfig = {
        emailAddress: username,
        emailPassword: password,
        platform: platformToSave,
        subjectFilter: filters.find((f) => f.field === "Subject")?.value || "",
        bodyFilter: filters.find((f) => f.field === "Body")?.value || "",
        fromFilter: filters.find((f) => f.field === "From")?.value || "",
        filterCondition: filters[0]?.condition || "Equals",
        logicCondition: filters.length > 1 ? "AND" : "OR",
        process: process?.id || null,
        robot,
        argumentsList,
        priority: priority,
        stickto_robot: stickToRobot  // Move it here
      };

      const requestData = {
        name,
        status: "Active",
        createdBy: userId,
        emailConfig,
        folderId: folder
      };

      LoggingService.log(
        `User ${username} created an Email Trigger: Name="${name}", Platform="${platform}", Process="${JSON.stringify(process)}", Robot="${robot}", Filters="${JSON.stringify(filters)}"`
      );

      const response = await axios.post(`${config.API_BASE_URL}/api/emailTrigger`, requestData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
      });

      toast.success("Email Trigger created successfully!");
      console.log("✅ Email Trigger created successfully:", response.data);
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to create Email Trigger. Please try again.");
      console.error("❌ Error creating email trigger:", error.response?.data || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="trigger-container">
      <Helmet>
        <title>Create Email Trigger</title>
      </Helmet>
      <h2>Create Email Trigger</h2>

      {!selectedTenant ? (
        <p className="error-message">Please select a tenant first.</p>
      ) : (
        <>
          <div className="form-group">
            <label>Trigger Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={formErrors.name ? 'error' : ''}
              placeholder="Enter Trigger Name"
            />
            {formErrors.name && <span className="error-text">{formErrors.name}</span>}
          </div>

          <div className="form-group">
            <label>Select connector *</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className={formErrors.platform ? 'error' : ''}
              disabled={isLoading}
            >
              <option value="">Select platform</option>
              <option value="Outlook">Microsoft Outlook 365</option>
              <option value="Gmail">Gmail</option>
            </select>
            {formErrors.platform && <span className="error-text">{formErrors.platform}</span>}
          </div>

          <div className="form-group">
            <label>Email Username *</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={formErrors.username ? 'error' : ''}
              disabled={isLoading}
              placeholder="Email Username"
            />
            {formErrors.username && <span className="error-text">{formErrors.username}</span>}
          </div>

          <div className="form-group">
            <label>Email Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={formErrors.password ? 'error' : ''}
              disabled={isLoading}
              placeholder="Email Password"
            />
            {formErrors.password && <span className="error-text">{formErrors.password}</span>}
          </div>

          <div className="form-group data-filters">
            <label>Data filters</label>
            <button onClick={handleAddCondition} disabled={isLoading}>Add condition</button>
            {filters.map((filter, index) => (
              <div key={index} className="filter-condition">
                <select
                  value={filter.field}
                  onChange={(e) => handleFilterChange(index, "field", e.target.value)}
                  className={formErrors[`filter_${index}`] ? 'error' : ''}
                >
                  <option value="">Select field</option>
                  <option value="Subject">Subject</option>
                  <option value="Body">Body</option>
                  <option value="From">From</option>
                </select>
                <select
                  value={filter.condition}
                  onChange={(e) => handleFilterChange(index, "condition", e.target.value)}
                  className={formErrors[`filter_${index}`] ? 'error' : ''}
                >
                  <option value="">Select condition</option>
                  <option value="contains">Contains</option>
                  <option value="equals">Equals</option>
                </select>
                <input
                  type="text"
                  value={filter.value}
                  onChange={(e) => handleFilterChange(index, "value", e.target.value)}
                  className={formErrors[`filter_${index}`] ? 'error' : ''}
                  placeholder="Value"
                />
                <button onClick={() => handleRemoveCondition(index)}>Remove</button>
                {formErrors[`filter_${index}`] &&
                  <span className="error-text">{formErrors[`filter_${index}`]}</span>
                }
              </div>
            ))}
          </div>

          <div className="form-group">
            <label>Input Arguments</label>
            <button onClick={() => setArgumentsList([...argumentsList, { name: "", value: "" }])}>+</button>
            {argumentsList.map((arg, index) => (
              <div key={index} className="argument">
                <input
                  type="text"
                  value={arg.name}
                  onChange={(e) => setArgumentsList(argumentsList.map((a, i) => (i === index ? { ...a, name: e.target.value } : a)))}
                  placeholder="Argument Name"
                />
                <input
                  type="text"
                  value={arg.value}
                  onChange={(e) => setArgumentsList(argumentsList.map((a, i) => (i === index ? { ...a, value: e.target.value } : a)))}
                  placeholder="Argument Value"
                />
                <button onClick={() => setArgumentsList(argumentsList.filter((_, i) => i !== index))}>Remove</button>
              </div>
            ))}
          </div>

          <FolderSelection
            folder={folder}
            onChange={setFolder}
            isGlobal={true}
            className={formErrors.folder ? 'error' : ''}
          />
          {formErrors.folder && <span className="error-text">{formErrors.folder}</span>}

          <RobotSelection
            robot={robot}
            onChange={setRobot}
            folder={folder}
            className={formErrors.robot ? 'error' : ''}
          />
          {formErrors.robot && <span className="error-text">{formErrors.robot}</span>}

          <ProcessSelection
            process={process}
            onChange={(selected) => setProcess(selected || { id: "", name: "" })}
            folder={folder}
            className={formErrors.process ? 'error' : ''}
          />
          {formErrors.process && <span className="error-text">{formErrors.process}</span>}

          <div className="form-group process-robot-lock">
            <div className="robot-lock-container">
              <input
                type="checkbox"
                id="stickToRobot"
                checked={stickToRobot}
                onChange={(e) => setStickToRobot(e.target.checked)}
                disabled={robot === 'All'}
              />
              <label htmlFor="stickToRobot" className="robot-lock-label">
                <span className="lock-icon">🔒</span>
                Lock process to selected robot
              </label>
            </div>
            {robot === 'All' ? (
              <small className="help-text warning">
                Select a specific robot to enable process locking
              </small>
            ) : (
              <small className="help-text">
                This process will only be executed by the selected robot
              </small>
            )}
          </div>
          {/* ... */}
          <div className="form-group">
            <label>Job priority *</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className={formErrors.priority ? 'error' : ''}
            >
              {priorities.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.icon} {p.label}
                </option>
              ))}
            </select>
            {formErrors.priority && <span className="error-text">{formErrors.priority}</span>}
          </div>




          <button
            onClick={handleEmailTrigger}
            disabled={isLoading}
            className={isLoading ? 'loading' : ''}
          >
            {isLoading ? 'Creating Trigger...' : 'Save Trigger'}
          </button>
        </>
      )
      }
    </div >
  );
};



export default EmailTrigger;
